import { createContext, useCallback, useEffect, useMemo, useState } from "react";
import api, { TOKEN_STORAGE_KEY, USER_STORAGE_KEY } from "../api/client";
import { SOUND_PACK_STORAGE_KEY, SOUND_PACK_SYNC_EVENT } from "../constants/audio";

const THEME_STORAGE_KEY = "rv-theme";

const emitThemeSyncEvent = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("rv-theme-sync"));
  }
};

const emitSoundPackSyncEvent = () => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(SOUND_PACK_SYNC_EVENT));
  }
};

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem(USER_STORAGE_KEY);
    if (!stored) return null;

    try {
      return JSON.parse(stored);
    } catch {
      localStorage.removeItem(USER_STORAGE_KEY);
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  const applyUserPreferences = useCallback((profile) => {
    if (!profile) {
      return;
    }

    if (profile.preferredTheme) {
      const normalizedTheme = profile.preferredTheme === "dark" ? "dark" : "light";
      localStorage.setItem(THEME_STORAGE_KEY, normalizedTheme);
      emitThemeSyncEvent();
    }

    if (profile.soundPack) {
      localStorage.setItem(SOUND_PACK_STORAGE_KEY, profile.soundPack);
      emitSoundPackSyncEvent();
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    localStorage.removeItem(SOUND_PACK_STORAGE_KEY);
    emitSoundPackSyncEvent();
    emitThemeSyncEvent();
    setUser(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY);
    if (token) {
      api
        .get("/auth/me")
        .then((res) => {
          setUser(res.data);
          localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(res.data));
          applyUserPreferences(res.data);
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [applyUserPreferences, logout]);

  const login = useCallback((token, userData) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
    setUser(userData);
    applyUserPreferences(userData);
    setLoading(false);
  }, [applyUserPreferences]);

  const refreshUser = useCallback(async () => {
    const res = await api.get("/auth/me");
    setUser(res.data);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(res.data));
    applyUserPreferences(res.data);
    return res.data;
  }, [applyUserPreferences]);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshUser }),
    [user, loading, login, logout, refreshUser]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}